/**
 * A parent putting their own address back on the members list, and asking
 * the server to email a parent who has not come back since a start-over.
 *
 * A start-over clears every member's sealed address but the caller's (each
 * was under the old family key). Each parent's device seals its own again
 * under the new key, once a session, the next time it is unlocked.
 *
 * The reminder email is the server's (services/api/valley/notify.py), sent
 * to the address Cognito holds for that account. It never carries the family
 * code: a code through the server would let the server open everything.
 */

import { api } from '../api/client';
import { currentAccount } from '../auth/cognito';
import { sealOwnEmail } from './families';

/** Once a session, per account and family. Best effort, never in the way. */
export async function noteOwnEmail(familyId: string, key: CryptoKey): Promise<void> {
  const mark = `valley.email-noted.${currentAccount()?.sub ?? ''}.${familyId}`;
  try {
    if (sessionStorage.getItem(mark)) return;
  } catch {
    /* no storage: write it every load, which is harmless */
  }
  try {
    const emailCt = await sealOwnEmail(key);
    if (!emailCt) return;
    await api.write('PUT', `/v1/families/${familyId}/members/me`, { emailCt });
    try {
      sessionStorage.setItem(mark, '1');
    } catch {
      /* as above */
    }
  } catch {
    // Tried again next session.
  }
}

/** Email one member how to get back in. Resolves to whether it was sent. */
export async function remindMember(familyId: string, sub: string): Promise<boolean> {
  const { sent } = await api.write<{ sent: boolean }>(
    'POST',
    `/v1/families/${familyId}/members/${sub}/remind`,
  );
  return sent;
}
